import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
  } from "react";
  import {
    ArrowDown,
    ArrowUp,
    CalendarDays,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Columns3,
    Focus,
    FolderPlus,
    Info,
    Link2,
    Palette,
    Plus,
    RefreshCw,
    Route,
    Trash2,
    X,
    ZoomIn,
    ZoomOut,
  } from "lucide-react";
  import clsx from "clsx";
  import { taskApi } from "api/tasks/taskApi";
  import { useAlert } from "components/UI_components/Alert";
  
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const ROW_HEIGHT = 44;
  const ADD_ROW_HEIGHT = 32;
  const HEADER_HEIGHT = 58;
  const DEFAULT_GROUP_COLOR = "#6B7280";
  const DEFAULT_TASK_COLOR = "#3B0069";
  const DEFAULT_SUBTASK_COLOR = "#1187C8";
  const MIN_PX_PER_DAY = 3;
  const MAX_PX_PER_DAY = 64;
  const DAY_PX_PER_DAY = 24;
  const WEEKDAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];
  const SIDEBAR_BASE_COLUMNS = {
    structure: 228,
    assignees: 90,
    duration: 66,
    progress: 72,
  };
  const SIDEBAR_BASE_WIDTH = Object.values(SIDEBAR_BASE_COLUMNS).reduce(
    (sum, width) => sum + width,
    0,
  );
  
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  
  const parseISODateLocal = (value) => {
    if (!value) return null;
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime()) ? null : date;
  };
  
  const formatISODate = (value) => {
    if (!(value instanceof Date)) return "";
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };
  
  const addDays = (date, days) => new Date(date.getTime() + days * MS_PER_DAY);

  const startOfWeek = (date) => {
    const result = new Date(date);
    const offset = (result.getDay() + 6) % 7;
    return addDays(result, -offset);
  };

  const quarterLabel = (date) =>
    `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()}`;
  
  const daysBetween = (start, end) => {
    if (!start || !end) return 0;
    return Math.max(1, Math.round((end - start) / MS_PER_DAY) + 1);
  };
  
  const getTaskProgress = (status) => {
    const value = String(status || "").toLowerCase();
    if (value.includes("complete")) return 100;
    if (value.includes("ongoing") || value.includes("progress")) return 50;
    return 0;
  };
  
  const getGroupProgress = (summary) => {
    const explicit = Number(summary?.progress);
    if (Number.isFinite(explicit)) return explicit;
    if (!summary?.total_tasks) return 0;
    return Math.round(((summary.completed_tasks || 0) / summary.total_tasks) * 100);
  };
  
  const statusClass = (status) => {
    const value = String(status || "").toLowerCase();
    if (value.includes("complete")) return "bg-emerald-50 text-emerald-700";
    if (value.includes("overdue") || value.includes("halted"))
      return "bg-rose-50 text-rose-700";
    if (value.includes("ongoing") || value.includes("active"))
      return "bg-amber-50 text-amber-700";
    return "bg-slate-100 text-slate-700";
  };
  
  const getDefaultColor = (type) => {
    if (type === "group") return DEFAULT_GROUP_COLOR;
    if (type === "subtask") return DEFAULT_SUBTASK_COLOR;
    return DEFAULT_TASK_COLOR;
  };
  
  const itemHasDates = (item) => Boolean(item?.start && item?.end);
  
  const getAssignees = (item) => {
    const members =
      (Array.isArray(item?.details?.allocated_members) &&
      item.details.allocated_members.length
        ? item.details.allocated_members
        : item?.details?.assign_to) ||
      item?.details?.task_members ||
      [];
    return Array.isArray(members) ? members : [];
  };

  const getMemberLookupKeys = (member) =>
    [
      member?.member_id,
      member?.id,
      member?.organization_member_id,
      member?.team_member_id,
      member?.user_id,
      member?.user?.id,
    ]
      .filter((value) => value !== undefined && value !== null && value !== "")
      .map((value) => String(value));

  const getAvatarUrl = (member) =>
    member?.userpic ||
    member?.profile_pic ||
    member?.avatar ||
    member?.user?.userpic ||
    member?.user_details?.userpic ||
    "";
  
  const getMemberName = (member) =>
    [member?.first_name, member?.last_name].filter(Boolean).join(" ") ||
    [member?.user?.first_name, member?.user?.last_name].filter(Boolean).join(" ") ||
    [member?.user_details?.first_name, member?.user_details?.last_name]
      .filter(Boolean)
      .join(" ") ||
    member?.name ||
    member?.user?.name ||
    member?.user_details?.name ||
    member?.member_name ||
    "Member";
  
  const memberInitials = (member) => {
    const name = getMemberName(member);
    return name
      .split(" ")
      .map((part) => part[0])
      .filter(Boolean)
      .join("")
      .slice(0, 2)
      .toUpperCase();
  };
  
  const dependencyKey = (dependency) =>
    dependency?.id ||
    `${dependency.predecessor_task}-${dependency.successor_task}-${dependency.dependency_type}`;
  
  const normalizeDependencies = (tasks) => {
    const map = new Map();
    tasks.forEach((task) => {
      [
        ...(task.predecessor_dependencies || []),
        ...(task.successor_dependencies || []),
      ].forEach((dependency) => {
        if (!dependency?.predecessor_task || !dependency?.successor_task) return;
        map.set(String(dependencyKey(dependency)), {
          id: dependency.id,
          predecessor_task: dependency.predecessor_task,
          predecessor_task_name: dependency.predecessor_task_name,
          successor_task: dependency.successor_task,
          successor_task_name: dependency.successor_task_name,
          dependency_type: dependency.dependency_type || "FS",
        });
      });
    });
    return Array.from(map.values());
  };
  
  const columnCatalog = {
    start: { label: "Start", width: 98 },
    end: { label: "End", width: 98 },
    status: { label: "Status", width: 112 },
  };
  
  function GanttTimeline() {
    const [items, setItems] = useState({});
    const [rootIds, setRootIds] = useState([]);
    const [dependencies, setDependencies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pxPerDay, setPxPerDay] = useState(DAY_PX_PER_DAY);
    const [selectedId, setSelectedId] = useState(null);
    const [dragState, setDragState] = useState(null);
    const [hoverBarId, setHoverBarId] = useState(null);
    const [hoverPlacement, setHoverPlacement] = useState(null);
    const [pendingDependency, setPendingDependency] = useState(null);
    const [dependencyDrag, setDependencyDrag] = useState(null);
    const [placementDrag, setPlacementDrag] = useState(null);
    const [addDraft, setAddDraft] = useState(null);
    const [addName, setAddName] = useState("");
    const [addSubmitting, setAddSubmitting] = useState(false);
    const [hoverAddRow, setHoverAddRow] = useState(null);
    const [showColumns, setShowColumns] = useState(false);
    const [showColors, setShowColors] = useState(false);
    const [showCriticalPath, setShowCriticalPath] = useState(false);
    const [dependencyEditorId, setDependencyEditorId] = useState(null);
    const [dependencyDrafts, setDependencyDrafts] = useState({
      predecessorTaskId: "",
      predecessorType: "FS",
      successorTaskId: "",
      successorType: "FS",
    });
    const [dependencySavingKey, setDependencySavingKey] = useState(null);
    const [visibleColumns, setVisibleColumns] = useState({
      start: false,
      end: false,
      status: false,
    });
    const [customColors, setCustomColors] = useState(() => {
      try {
        return JSON.parse(localStorage.getItem("ganttTimelineColors") || "{}");
      } catch {
        return {};
      }
    });
    const [timelineViewport, setTimelineViewport] = useState({
      left: 0,
      width: 0,
    });
    const [hasTaskAuthority, setHasTaskAuthority] = useState(false);
    const [siteMembers, setSiteMembers] = useState([]);
  
    const leftRef = useRef(null);
    const rightRef = useRef(null);
    const svgRef = useRef(null);
    const didAutoFitRef = useRef(false);
    const itemsRef = useRef(items);
    const addInputRef = useRef(null);
    const placementDraftRef = useRef(null);
    const placementCommitIdsRef = useRef(new Set());
    const pendingViewportRestoreRef = useRef(null);
    const lastRevealScrollTopRef = useRef(0);
    const user = useMemo(
      () => JSON.parse(localStorage.getItem("user") || "{}"),
      [],
    );
    const { addAlert } = useAlert();

    const showTimelineError = useCallback(
      (message) => {
        addAlert(message || "Failed to update schedule.", "error");
      },
      [addAlert],
    );

    useEffect(() => {
      itemsRef.current = items;
    }, [items]);

    useEffect(() => {
      if (!addDraft) return;
      const timer = window.setTimeout(() => {
        addInputRef.current?.focus();
      }, 0);
      return () => window.clearTimeout(timer);
    }, [addDraft]);
  
    useEffect(() => {
      const left = leftRef.current;
      const right = rightRef.current;
      if (!left || !right) return undefined;
      let syncing = false;
      const syncLeft = () => {
        if (syncing) return;
        syncing = true;
        right.scrollTop = left.scrollTop;
        syncing = false;
      };
      const syncRight = () => {
        if (syncing) return;
        syncing = true;
        left.scrollTop = right.scrollTop;
        syncing = false;
      };
      left.addEventListener("scroll", syncLeft);
      right.addEventListener("scroll", syncRight);
      return () => {
        left.removeEventListener("scroll", syncLeft);
        right.removeEventListener("scroll", syncRight);
      };
    }, []);
  
    useEffect(() => {
      localStorage.setItem("ganttTimelineColors", JSON.stringify(customColors));
    }, [customColors]);
  
    const loadData = useCallback(async () => {
      setLoading(true);
      setError(null);
      setPxPerDay(DAY_PX_PER_DAY);
      didAutoFitRef.current = false;
      try {
        const taskGroups = await taskApi.getAllTaskGroups();
        const allTasks = [];
        const loadedGroups = await Promise.all(
          taskGroups.map(async (group, groupIndex) => {
            const tasks = await taskApi.getAllTasks(group.id);
            allTasks.push(...tasks);
            const loadedTasks = await Promise.all(
              tasks.map(async (task, taskIndex) => {
                const subtasks = await taskApi.getAllSubtasks(task.id);
                return { task, taskIndex, subtasks };
              }),
            );
            return { group, groupIndex, tasks: loadedTasks };
          }),
        );
  
        const map = {};
        const roots = [];
  
        loadedGroups.forEach(({ group, groupIndex, tasks }) => {
          const gid = `group_${group.id}`;
          const groupProgress = getGroupProgress(group.task_group_summary);
          const groupStart = parseISODateLocal(group.start_date);
          const groupEnd = parseISODateLocal(group.end_date);
          roots.push(gid);
          map[gid] = {
            id: gid,
            label: group.group_name,
            type: "group",
            parentId: null,
            order: groupIndex,
            start: groupStart,
            end: groupEnd,
            status:
              groupProgress === 100
                ? "Completed"
                : groupEnd && groupEnd < new Date() && groupProgress < 100
                  ? "Overdue"
                  : groupStart && groupStart > new Date()
                    ? "Not Started"
                    : "Active",
            progress: groupProgress,
            expanded: true,
            details: {
              originalId: group.id,
              priority: group.priority,
              allocated_members: group.allocated_members,
            },
          };
  
          tasks.forEach(({ task, taskIndex, subtasks }) => {
            const tid = `task_${task.id}`;
            map[tid] = {
              id: tid,
              label: task.task_name,
              type: "task",
              parentId: gid,
              order: taskIndex,
              start: parseISODateLocal(task.start_date),
              end: parseISODateLocal(task.end_date),
              status: task.task_status,
              progress: getTaskProgress(task.task_status),
              expanded: true,
              details: {
                originalId: task.id,
                priority: task.priority,
                task_details: task.task_details,
                estimated_amount: task.estimated_amount,
                allocated_members: task.allocated_members || task.members,
                task_members: task.task_members,
                task_group: task.task_group,
              },
            };
  
            subtasks.forEach((subtask, subtaskIndex) => {
              const sid = `subtask_${subtask.id}`;
              map[sid] = {
                id: sid,
                label: subtask.subtask_name,
                type: "subtask",
                parentId: tid,
                order: subtaskIndex,
                start: parseISODateLocal(subtask.start_date),
                end: parseISODateLocal(subtask.end_date),
                status: subtask.is_completed ? "Complete" : "Ongoing",
                progress: subtask.is_completed ? 100 : 0,
                expanded: false,
                details: {
                  originalId: subtask.id,
                  is_completed: subtask.is_completed,
                  subtask_details: subtask.subtask_details,
                  task_id: subtask.task_id,
                  assign_to: subtask.assign_to,
                },
              };
            });
          });
        });
  
        setItems(map);
        setRootIds(roots);
        setDependencies(normalizeDependencies(allTasks));
      } catch (err) {
        console.error(err);
        setError("Failed to load Gantt data.");
      } finally {
        setLoading(false);
      }
    }, []);
  
    useEffect(() => {
      loadData();
    }, [loadData]);
  
    useEffect(() => {
      const fetchAuthority = async () => {
        try {
          const siteData = JSON.parse(localStorage.getItem("siteData") || "{}");
          const siteId = siteData?.id || localStorage.getItem("site");
          if (!siteId) return;
          const response = await taskApi.getSiteMembers(siteId);
          const members = Array.isArray(response?.data) ? response.data : [];
          setSiteMembers(members);
          const currentMember = members.find((member) => member.user_id === user.user);
          setHasTaskAuthority(
            Boolean(
              currentMember?.create_taskgroup ||
                currentMember?.is_admin ||
                currentMember?.joined_as === "Owner",
            ),
          );
        } catch {
          setHasTaskAuthority(false);
        }
      };
      fetchAuthority();
    }, [user.user]);
  
    const siteMemberById = useMemo(() => {
      const map = {};
      siteMembers.forEach((member) => {
        getMemberLookupKeys(member).forEach((key) => {
          map[key] = member;
        });
      });
      return map;
    }, [siteMembers]);

    const resolveAssignees = useCallback(
      (item) =>
        getAssignees(item)
          .map((member) => {
            const match = getMemberLookupKeys(member)
              .map((key) => siteMemberById[key])
              .find(Boolean);
            return match
              ? {
                  ...match,
                  ...member,
                  userpic: getAvatarUrl(member) || getAvatarUrl(match),
                }
              : member;
          })
          .filter(Boolean),
      [siteMemberById],
    );

    const rowOrder = useMemo(() => {
      const result = [];
      const pushChildren = (parentId, level) => {
        Object.values(items)
          .filter((item) => item.parentId === parentId)
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .forEach((child) => {
            result.push({ id: child.id, level });
            if (child.expanded) pushChildren(child.id, level + 1);
          });
      };
      rootIds.forEach((id) => {
        result.push({ id, level: 0, kind: "item" });
        if (items[id]?.expanded) pushChildren(id, 1);
        if (hasTaskAuthority) {
          result.push({
            id: `add_${id}`,
            groupId: id,
            level: items[id]?.expanded ? 1 : 0,
            kind: "add",
          });
        }
      });
      return result;
    }, [hasTaskAuthority, items, rootIds]);
  
    const rows = useMemo(() => {
      let top = 0;
      return rowOrder.map(({ id, groupId, kind, level }) => {
        const height = kind === "add" ? ADD_ROW_HEIGHT : ROW_HEIGHT;
        const row =
          kind === "add"
            ? {
                id,
                groupId,
                type: "add",
                label: "",
                level,
                top,
                height,
              }
            : {
                ...items[id],
                level,
                top,
                height,
              };
        top += height;
        return row;
      });
    }, [items, rowOrder]);
  
    const rowMap = useMemo(() => {
      const map = {};
      rows.forEach((row) => {
        map[row.id] = row;
      });
      return map;
    }, [rows]);
  
    const { minDate, maxDate } = useMemo(() => {
      const datedItems = Object.values(items).filter(itemHasDates);
      if (!datedItems.length) {
        const today = new Date();
        return {
          minDate: new Date(today.getFullYear(), today.getMonth(), 1),
          maxDate: new Date(today.getFullYear(), today.getMonth() + 2, 0),
        };
      }
      let min = new Date(8640000000000000);
      let max = new Date(-8640000000000000);
      datedItems.forEach((item) => {
        if (item.start < min) min = item.start;
        if (item.end > max) max = item.end;
      });
      return {
        minDate: new Date(min.getFullYear(), min.getMonth() - 1, 1),
        maxDate: new Date(max.getFullYear(), max.getMonth() + 2, 0),
      };
    }, [items]);
  
    const totalDays = useMemo(() => daysBetween(minDate, maxDate), [minDate, maxDate]);
    const contentWidth = useMemo(
      () => Math.max(1200, totalDays * pxPerDay),
      [pxPerDay, totalDays],
    );
    const rowsHeight = rows.reduce((sum, row) => sum + (row.height || ROW_HEIGHT), 0);
    const bodyHeight = Math.max(rowsHeight, 420);

    const updateTimelineViewport = useCallback(() => {
      const right = rightRef.current;
      if (!right) return;
      const next = {
        left: right.scrollLeft,
        width: right.clientWidth,
      };
      setTimelineViewport((current) =>
        current.left === next.left && current.width === next.width
          ? current
          : next,
      );
    }, []);
  
    const xForDate = useCallback(
      (date) => Math.round(((date - minDate) / MS_PER_DAY) * pxPerDay),
      [minDate, pxPerDay],
    );
  
    const dateForX = useCallback(
      (x) => {
        const days = clamp(Math.round(x / pxPerDay), 0, totalDays - 1);
        return addDays(minDate, days);
      },
      [minDate, pxPerDay, totalDays],
    );
  
    const widthForSpan = useCallback(
      (start, end) => Math.max(8, daysBetween(start, end) * pxPerDay),
      [pxPerDay],
    );

    useLayoutEffect(() => {
      const restore = pendingViewportRestoreRef.current;
      const right = rightRef.current;
      if (!restore || !right) return;

      pendingViewportRestoreRef.current = null;
      right.scrollLeft = clamp(
        xForDate(restore.anchorDate),
        0,
        Math.max(0, right.scrollWidth - right.clientWidth),
      );
      right.scrollTop = restore.scrollTop;
      lastRevealScrollTopRef.current = restore.scrollTop;
      updateTimelineViewport();
    }, [bodyHeight, contentWidth, updateTimelineViewport, xForDate]);

    useLayoutEffect(() => {
      updateTimelineViewport();
    }, [bodyHeight, contentWidth, updateTimelineViewport]);

    const timelineScale = useMemo(() => {
      if (pxPerDay >= 16) return "day";
      if (pxPerDay >= 8) return "week";
      return "month";
    }, [pxPerDay]);
  
    const monthTicks = useMemo(() => {
      const out = [];
      for (
        let d = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
        d <= maxDate;
        d = new Date(d.getFullYear(), d.getMonth() + 1, 1)
      ) {
        out.push(new Date(d));
      }
      return out;
    }, [minDate, maxDate]);

    const weekTicks = useMemo(() => {
      const out = [];
      for (let d = startOfWeek(minDate); d <= maxDate; d = addDays(d, 7)) {
        out.push(new Date(d));
      }
      return out;
    }, [minDate, maxDate]);

    const quarterTicks = useMemo(() => {
      const out = [];
      for (
        let d = new Date(
          minDate.getFullYear(),
          Math.floor(minDate.getMonth() / 3) * 3,
          1,
        );
        d <= maxDate;
        d = new Date(d.getFullYear(), d.getMonth() + 3, 1)
      ) {
        out.push(new Date(d));
      }
      return out;
    }, [minDate, maxDate]);
  
    const dayTicks = useMemo(() => {
      const out = [];
      for (let d = new Date(minDate); d <= maxDate; d = addDays(d, 1)) {
        out.push(new Date(d));
      }
      return out;
    }, [minDate, maxDate]);
  
    const visibleSidebarColumns = useMemo(
      () =>
        Object.entries(visibleColumns)
          .filter(([, visible]) => visible)
          .map(([key]) => ({ key, ...columnCatalog[key] })),
      [visibleColumns],
    );
  
    const sidebarGridColumns = useMemo(
      () =>
        `${SIDEBAR_BASE_COLUMNS.structure}px ${SIDEBAR_BASE_COLUMNS.assignees}px ${SIDEBAR_BASE_COLUMNS.duration}px ${SIDEBAR_BASE_COLUMNS.progress}px ${visibleSidebarColumns
          .map((column) => `${column.width}px`)
          .join(" ")}`,
      [visibleSidebarColumns],
    );

    const leftWidth = useMemo(
      () =>
        SIDEBAR_BASE_WIDTH +
        visibleSidebarColumns.reduce((sum, column) => sum + column.width, 0),
      [visibleSidebarColumns],
    );
  
    const taskIdToItemId = useMemo(() => {
      const map = {};
      Object.values(items).forEach((item) => {
        if (item.type === "task") map[item.details.originalId] = item.id;
      });
      return map;
    }, [items]);

    const resolveTaskGroupForAdd = useCallback(() => {
      const selected = items[selectedId];
      if (selected?.type === "group") return selected;
      if (selected?.type === "task") return items[selected.parentId];
      if (selected?.type === "subtask") {
        const parentTask = items[selected.parentId];
        return parentTask ? items[parentTask.parentId] : null;
      }
      return items[rootIds[rootIds.length - 1]] || items[rootIds[0]] || null;
    }, [items, rootIds, selectedId]);
  
    const dependencyEdges = useMemo(
      () =>
        dependencies
          .map((dependency) => ({
            ...dependency,
            predecessorId: taskIdToItemId[dependency.predecessor_task],
            successorId: taskIdToItemId[dependency.successor_task],
          }))
          .filter((dependency) => dependency.predecessorId && dependency.successorId),
      [dependencies, taskIdToItemId],
    );

    const taskOptions = useMemo(
      () =>
        Object.values(items)
          .filter((item) => item.type === "task")
          .sort((a, b) => a.label.localeCompare(b.label)),
      [items],
    );

    const editedDependencyItem = dependencyEditorId
      ? items[dependencyEditorId]
      : null;

    const editedPredecessors = useMemo(
      () =>
        dependencyEditorId
          ? dependencyEdges.filter((edge) => edge.successorId === dependencyEditorId)
          : [],
      [dependencyEdges, dependencyEditorId],
    );

    const editedSuccessors = useMemo(
      () =>
        dependencyEditorId
          ? dependencyEdges.filter((edge) => edge.predecessorId === dependencyEditorId)
          : [],
      [dependencyEdges, dependencyEditorId],
    );
  
    const criticalPath = useMemo(() => {
      const taskItems = Object.values(items).filter((item) => item.type === "task");
      const outgoing = new Map();
      dependencyEdges.forEach((edge) => {
        if (!outgoing.has(edge.predecessorId)) outgoing.set(edge.predecessorId, []);
        outgoing.get(edge.predecessorId).push(edge);
      });
  
      const visiting = new Set();
      const memo = new Map();
      const solve = (id) => {
        if (memo.has(id)) return memo.get(id);
        if (visiting.has(id)) return { duration: 0, nodes: [], edges: [] };
        visiting.add(id);
        const item = items[id];
        const ownDuration = itemHasDates(item) ? daysBetween(item.start, item.end) : 0;
        let best = { duration: ownDuration, nodes: [id], edges: [] };
        (outgoing.get(id) || []).forEach((edge) => {
          const tail = solve(edge.successorId);
          const candidate = {
            duration: ownDuration + tail.duration,
            nodes: [id, ...tail.nodes],
            edges: [dependencyKey(edge), ...tail.edges],
          };
          if (candidate.duration > best.duration) best = candidate;
        });
        visiting.delete(id);
        memo.set(id, best);
        return best;
      };
  
      return taskItems.reduce(
        (best, item) => {
          const candidate = solve(item.id);
          return candidate.duration > best.duration ? candidate : best;
        },
        { duration: 0, nodes: [], edges: [] },
      );
    }, [dependencyEdges, items]);
  
    const criticalNodeSet = useMemo(
      () => new Set(showCriticalPath ? criticalPath.nodes : []),
      [criticalPath.nodes, showCriticalPath],
    );
  
    const criticalEdgeSet = useMemo(
      () => new Set(showCriticalPath ? criticalPath.edges : []),
      [criticalPath.edges, showCriticalPath],
    );
  
    const toggleExpand = (id) => {
      setItems((prev) => ({
        ...prev,
        [id]: { ...prev[id], expanded: !prev[id].expanded },
      }));
    };
  
    const itemColor = (item) => customColors[item.id] || getDefaultColor(item.type);
  
    const defaultPlacementDays = (item) => {
      if (item?.type === "group") return 14;
      if (item?.type === "subtask") return 3;
      return 7;
    };
  
    const pointerToSvgPoint = useCallback((event) => {
      const bounds = svgRef.current?.getBoundingClientRect();
      if (!bounds) return null;
      return {
        x: clamp(event.clientX - bounds.left, 0, contentWidth),
        y: clamp(event.clientY - bounds.top, 0, bodyHeight),
      };
    }, [bodyHeight, contentWidth]);
  
    const rowAtSvgY = useCallback(
      (y) => rows.find((row) => y >= row.top && y <= row.top + row.height),
      [rows],
    );
  
    const getDescendantIds = useCallback((sourceItems, id) => {
      const out = [];
      const collect = (parentId) => {
        Object.values(sourceItems).forEach((item) => {
          if (item.parentId === parentId) {
            out.push(item.id);
            collect(item.id);
          }
        });
      };
      collect(id);
      return out;
    }, []);
  
    const recalcAncestors = useCallback((draft, id) => {
      let current = draft[id];
      while (current?.parentId) {
        const parent = draft[current.parentId];
        if (!parent) return;
        const children = Object.values(draft).filter(
          (item) => item.parentId === parent.id && itemHasDates(item),
        );
        if (children.length) {
          const start = new Date(Math.min(...children.map((item) => item.start)));
          const end = new Date(Math.max(...children.map((item) => item.end)));
          draft[parent.id] = { ...parent, start, end };
        }
        current = draft[parent.id];
      }
    }, []);
  
    const getAncestorIds = useCallback((sourceItems, id) => {
      const out = [];
      let current = sourceItems[id];
      while (current?.parentId) {
        out.push(current.parentId);
        current = sourceItems[current.parentId];
      }
      return out;
    }, []);
  
    const applyDependencyCascade = useCallback(
      (draft, changedIds) => {
        const queue = [...changedIds];
        const changed = new Set(changedIds);
        const guard = new Set();
        while (queue.length) {
          const predecessorId = queue.shift();
          const predecessor = draft[predecessorId];
          if (!predecessor || predecessor.type !== "task" || !itemHasDates(predecessor)) {
            continue;
          }
          dependencyEdges
            .filter((edge) => edge.predecessorId === predecessorId)
            .forEach((edge) => {
              const successor = draft[edge.successorId];
              if (!successor || !itemHasDates(successor)) return;
              const requiredStart =
                edge.dependency_type === "SS" ? predecessor.start : predecessor.end;
              if (successor.start >= requiredStart) return;
              const delta = Math.round((requiredStart - successor.start) / MS_PER_DAY);
              const shifted = {
                ...successor,
                start: addDays(successor.start, delta),
                end: addDays(successor.end, delta),
              };
              draft[successor.id] = shifted;
              getDescendantIds(draft, successor.id).forEach((childId) => {
                const child = draft[childId];
                if (!itemHasDates(child)) return;
                draft[childId] = {
                  ...child,
                  start: addDays(child.start, delta),
                  end: addDays(child.end, delta),
                };
                changed.add(childId);
              });
              recalcAncestors(draft, successor.id);
              changed.add(successor.id);
              getAncestorIds(draft, successor.id).forEach((ancestorId) =>
                changed.add(ancestorId),
              );
              if (!guard.has(successor.id)) {
                guard.add(successor.id);
                queue.push(successor.id);
              }
            });
        }
        return changed;
      },
      [dependencyEdges, getAncestorIds, getDescendantIds, recalcAncestors],
    );
  
    const persistChangedItems = useCallback(async (nextItems, originalItems, changedIds) => {
      const ids = Array.from(changedIds);
      const idSet = new Set(ids);
      const taskOrderingEdges = [];

      dependencyEdges.forEach((edge) => {
        if (!idSet.has(edge.predecessorId) || !idSet.has(edge.successorId)) return;

        const predecessor = nextItems[edge.predecessorId];
        const successor = nextItems[edge.successorId];
        const originalPredecessor = originalItems[edge.predecessorId] || predecessor;
        const originalSuccessor = originalItems[edge.successorId] || successor;
        if (
          !itemHasDates(predecessor) ||
          !itemHasDates(successor) ||
          !itemHasDates(originalPredecessor) ||
          !itemHasDates(originalSuccessor)
        ) {
          return;
        }

        const nextRequiredStart =
          edge.dependency_type === "SS" ? predecessor.start : predecessor.end;
        const originalRequiredStart =
          edge.dependency_type === "SS"
            ? originalPredecessor.start
            : originalPredecessor.end;

        if (nextRequiredStart > originalSuccessor.start) {
          taskOrderingEdges.push([edge.successorId, edge.predecessorId]);
        } else if (successor.start < originalRequiredStart) {
          taskOrderingEdges.push([edge.predecessorId, edge.successorId]);
        }
      });

      const orderTasks = (taskIds) => {
        const taskIdSet = new Set(taskIds);
        const outgoing = new Map();
        taskOrderingEdges.forEach(([before, after]) => {
          if (!taskIdSet.has(before) || !taskIdSet.has(after)) return;
          if (!outgoing.has(before)) outgoing.set(before, []);
          outgoing.get(before).push(after);
        });

        const ordered = [];
        const visited = new Set();
        const visiting = new Set();
        const visit = (id) => {
          if (visited.has(id) || visiting.has(id)) return;
          visiting.add(id);
          (outgoing.get(id) || []).forEach(visit);
          visiting.delete(id);
          visited.add(id);
          ordered.unshift(id);
        };

        taskIds.forEach(visit);
        return ordered;
      };

      const taskIds = ids.filter((id) => nextItems[id]?.type === "task");
      const orderedTaskIds = orderTasks(taskIds);
      const orderedIds = [
        ...orderedTaskIds,
        ...ids.filter((id) => nextItems[id]?.type === "subtask"),
        ...ids.filter((id) => nextItems[id]?.type === "group"),
        ...ids.filter(
          (id) =>
            nextItems[id]?.type !== "task" &&
            nextItems[id]?.type !== "subtask" &&
            nextItems[id]?.type !== "group",
        ),
      ];

      const persisted = new Set();
      for (const id of orderedIds) {
        const item = nextItems[id];
        const original = originalItems[id];
        if (!item || !itemHasDates(item) || !item.details?.originalId) continue;
        if (
          original &&
          formatISODate(original.start) === formatISODate(item.start) &&
          formatISODate(original.end) === formatISODate(item.end)
        ) {
          continue;
        }
        if (persisted.has(id)) continue;
        persisted.add(id);
        const payload = {
          start_date: formatISODate(item.start),
          end_date: formatISODate(item.end),
        };
        if (item.type === "group") {
          await taskApi.updateTaskGroup(item.details.originalId, payload);
        } else if (item.type === "task") {
          await taskApi.updateTask(item.details.originalId, payload);
        } else if (item.type === "subtask") {
          await taskApi.updateSubtask(item.details.originalId, payload);
        }
      }
    }, [dependencyEdges]);

    const autoScrollTimeline = useCallback((event) => {
      const right = rightRef.current;
      if (!right) return 0;
      const bounds = right.getBoundingClientRect();
      const threshold = 72;
      const maxStep = 28;
      let delta = 0;

      if (event.clientX > bounds.right - threshold) {
        delta =
          ((threshold - (bounds.right - event.clientX)) / threshold) * maxStep;
      } else if (event.clientX < bounds.left + threshold) {
        delta =
          -((threshold - (event.clientX - bounds.left)) / threshold) * maxStep;
      }

      if (!delta) return 0;
      const previous = right.scrollLeft;
      right.scrollLeft = clamp(
        previous + delta,
        0,
        right.scrollWidth - right.clientWidth,
      );
      return right.scrollLeft - previous;
    }, []);
  
    const beginDrag = (event, id, mode) => {
      event.preventDefault();
      event.stopPropagation();
      if (!hasTaskAuthority) return;
      const item = items[id];
      if (!itemHasDates(item)) return;
      const ids = [id, ...getDescendantIds(items, id)].filter((itemId) =>
        itemHasDates(items[itemId]),
      );
      const snapshots = {};
      ids.forEach((snapshotId) => {
        const snapshotItem = items[snapshotId];
        snapshots[snapshotId] = {
          start: snapshotItem.start,
          end: snapshotItem.end,
        };
      });
      setDragState({
        id,
        mode,
        startX: event.clientX,
        startScrollLeft: rightRef.current?.scrollLeft || 0,
        originalItems: items,
        snapshots,
      });
    };
  
    useEffect(() => {
      if (!dragState) return undefined;
  
      const onMove = (event) => {
        autoScrollTimeline(event);
        const scrollDelta =
          (rightRef.current?.scrollLeft || 0) - (dragState.startScrollLeft || 0);
        const deltaDays = Math.round(
          (event.clientX - dragState.startX + scrollDelta) / pxPerDay,
        );
        const sourceItems = dragState.originalItems || items;
        const changed = new Set([dragState.id]);
        const next = Object.fromEntries(
          Object.entries(sourceItems).map(([itemId, item]) => [itemId, { ...item }]),
        );
        const dragged = next[dragState.id];
        const snapshot = dragState.snapshots[dragState.id];
        if (!dragged || !snapshot) return;
  
        let nextStart = snapshot.start;
        let nextEnd = snapshot.end;
        if (dragState.mode === "move") {
          nextStart = addDays(snapshot.start, deltaDays);
          nextEnd = addDays(snapshot.end, deltaDays);
          Object.entries(dragState.snapshots).forEach(([id, snap]) => {
            if (id === dragState.id) return;
            next[id] = {
              ...next[id],
              start: addDays(snap.start, deltaDays),
              end: addDays(snap.end, deltaDays),
            };
            changed.add(id);
          });
        } else if (dragState.mode === "resize-left") {
          nextStart = addDays(snapshot.start, deltaDays);
          if (nextStart > nextEnd) nextStart = new Date(nextEnd);
        } else if (dragState.mode === "resize-right") {
          nextEnd = addDays(snapshot.end, deltaDays);
          if (nextEnd < nextStart) nextEnd = new Date(nextStart);
        }
  
        next[dragged.id] = { ...dragged, start: nextStart, end: nextEnd };
        recalcAncestors(next, dragged.id);
        getAncestorIds(next, dragged.id).forEach((ancestorId) =>
          changed.add(ancestorId),
        );
        const cascaded = applyDependencyCascade(next, changed);
        itemsRef.current = next;
        setItems(next);
        setDragState((current) => (current ? { ...current, changedIds: cascaded } : null));
      };
  
      const onUp = async () => {
        const changedIds = dragState.changedIds || new Set([dragState.id]);
        const originalItems =
          dragState.originalItems ||
          Object.fromEntries(
            Object.entries(dragState.snapshots).map(([id, snapshot]) => [
              id,
              {
                ...items[id],
                start: snapshot.start,
                end: snapshot.end,
              },
            ]),
          );
        const latestItems = itemsRef.current;
        setDragState(null);
        try {
          await persistChangedItems(latestItems, originalItems, changedIds);
        } catch (err) {
          console.error("Failed to persist Gantt drag", err);
          showTimelineError(`${err.message || "Failed to update schedule."} Reverting the change.`);
          setItems((prev) => {
            const reverted = { ...prev };
            changedIds.forEach((id) => {
              if (reverted[id]) {
                reverted[id] = originalItems[id] || reverted[id];
              }
            });
            itemsRef.current = reverted;
            return reverted;
          });
        }
      };
  
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
      return () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
    }, [
      applyDependencyCascade,
      autoScrollTimeline,
      getAncestorIds,
      dragState,
      items,
      persistChangedItems,
      pxPerDay,
      recalcAncestors,
      showTimelineError,
    ]);
  
    const handleTimelineMove = (event, row) => {
      if (!hasTaskAuthority || itemHasDates(row) || placementDrag) return;
      const point = pointerToSvgPoint(event);
      if (!point) return;
      const date = dateForX(point.x);
      const duration = defaultPlacementDays(row);
      setHoverPlacement({
        id: row.id,
        start: date,
        end: addDays(date, duration - 1),
        x: xForDate(date),
        width: duration * pxPerDay,
      });
    };

    const commitPlacement = useCallback(async (id, rawStart, rawEnd) => {
      if (placementCommitIdsRef.current.has(id)) return;
      const currentItems = itemsRef.current;
      const item = currentItems[id];
      if (!item || itemHasDates(item)) {
        setPlacementDrag(null);
        return;
      }

      placementCommitIdsRef.current.add(id);
      const start = rawStart <= rawEnd ? rawStart : rawEnd;
      const end = rawStart <= rawEnd ? rawEnd : rawStart;
      const right = rightRef.current;
      if (right) {
        pendingViewportRestoreRef.current = {
          anchorDate: dateForX(right.scrollLeft),
          scrollTop: right.scrollTop,
        };
      }
      const next = { ...currentItems, [item.id]: { ...item, start, end } };
      recalcAncestors(next, item.id);
      const changed = new Set([item.id, ...getAncestorIds(next, item.id)]);
      itemsRef.current = next;
      setItems(next);
      setPlacementDrag(null);
      setHoverPlacement(null);
      setSelectedId(item.id);

      try {
        await persistChangedItems(next, currentItems, changed);
      } catch (err) {
        console.error(err);
        showTimelineError(err.message || "Failed to place this item on the timeline.");
        itemsRef.current = currentItems;
        setItems(currentItems);
      } finally {
        placementCommitIdsRef.current.delete(id);
      }
    }, [
      dateForX,
      getAncestorIds,
      persistChangedItems,
      recalcAncestors,
      showTimelineError,
    ]);

    const finishPlacement = useCallback(() => {
      const draft = placementDraftRef.current;
      if (!draft) return;
      placementDraftRef.current = null;
      commitPlacement(
        draft.id,
        draft.start,
        draft.hasDragged ? draft.end : draft.defaultEnd,
      );
    }, [commitPlacement]);
  
    const beginPlacement = (event, row) => {
      if (!hasTaskAuthority || itemHasDates(row) || event.button !== 0) return;
      const point = pointerToSvgPoint(event);
      if (!point) return;
      event.preventDefault();
      event.stopPropagation();

      const startDate = dateForX(point.x);
      const defaultEndDate = addDays(startDate, defaultPlacementDays(row) - 1);
      const startClientX = event.clientX;
      let currentEndDate = defaultEndDate;
      let hasDragged = false;

      placementDraftRef.current = {
        id: row.id,
        start: startDate,
        end: defaultEndDate,
        defaultEnd: defaultEndDate,
        hasDragged: false,
      };
      setPlacementDrag({
        id: row.id,
        start: startDate,
        end: defaultEndDate,
      });

      const onMove = (moveEvent) => {
        autoScrollTimeline(moveEvent);
        const movePoint = pointerToSvgPoint(moveEvent);
        if (!movePoint) return;
        if (Math.abs(moveEvent.clientX - startClientX) >= 3) {
          hasDragged = true;
        }
        currentEndDate = hasDragged ? dateForX(movePoint.x) : defaultEndDate;
        if (placementDraftRef.current?.id === row.id) {
          placementDraftRef.current = {
            ...placementDraftRef.current,
            end: currentEndDate,
            hasDragged,
          };
        }
        setPlacementDrag((current) =>
          current ? { ...current, end: currentEndDate } : current,
        );
      };

      const onUp = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
        finishPlacement();
      };

      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    };

    const handlePlacementClick = (event, row) => {
      if (!hasTaskAuthority || event.button !== 0) return;
      const latest = itemsRef.current[row.id];
      if (!latest || itemHasDates(latest)) return;
      const point = pointerToSvgPoint(event);
      if (!point) return;
      const start = dateForX(point.x);
      commitPlacement(row.id, start, addDays(start, defaultPlacementDays(row) - 1));
    };

    const startAddDraft = (type, groupId = null, rowId = null) => {
      if (!hasTaskAuthority) return;
      if (type === "task") {
        const group = items[groupId] || resolveTaskGroupForAdd();
        if (!group) {
          showTimelineError("Create a task group before adding a task.");
          return;
        }
        setAddDraft({ type, groupId: group.id, rowId });
      } else {
        setAddDraft({ type, groupId, rowId });
      }
      setAddName("");
    };

    const cancelAddDraft = () => {
      if (addSubmitting) return;
      setAddDraft(null);
      setAddName("");
    };

    const submitAddDraft = async (event) => {
      event.preventDefault();
      if (!addDraft || addSubmitting) return;
      const name = addName.trim();
      if (!name) return;

      setAddSubmitting(true);
      try {
        const today = formatISODate(new Date());
        if (addDraft.type === "group") {
          const site = Number(localStorage.getItem("site"));
          await taskApi.addTaskGroup({
            site_id: site,
            task_group_name: name,
            status: "Active",
            start_date: today,
            end_date: today,
            priority: 3,
          });
        } else {
          const group = itemsRef.current[addDraft.groupId] || resolveTaskGroupForAdd();
          if (!group?.details?.originalId) {
            throw new Error("Choose a task group before adding a task.");
          }
          const taskCount = Object.values(itemsRef.current).filter(
            (item) => item.parentId === group.id && item.type === "task",
          ).length;
          await taskApi.addTask({
            task_group: group.details.originalId,
            task_name: name,
            task_details: "-",
            task_status: "Not started",
            priority: 1,
            estimated_amount: "0",
            torder_index: taskCount,
          });
        }

        setAddDraft(null);
        setAddName("");
        await loadData();
      } catch (err) {
        console.error(err);
        showTimelineError(err.message || "Failed to add item.");
      } finally {
        setAddSubmitting(false);
      }
    };
  
    const createDependency = async (successor) => {
      if (!pendingDependency || successor.type !== "task") return;
      const predecessor = items[pendingDependency.itemId];
      if (!predecessor || predecessor.id === successor.id) {
        setPendingDependency(null);
        return;
      }
      try {
        const dependency = await taskApi.createTaskDependency({
          predecessor_task: predecessor.details.originalId,
          successor_task: successor.details.originalId,
          dependency_type: pendingDependency.type,
        });
        setDependencies((prev) => [...prev, dependency]);
        setPendingDependency(null);
      } catch (err) {
        showTimelineError(err.message || "Failed to create dependency.");
        setPendingDependency(null);
      }
    };
  
    const createDependencyFrom = useCallback(async (source, successor) => {
      if (!source || successor?.type !== "task") return;
      const predecessor = items[source.itemId];
      if (!predecessor || predecessor.id === successor.id) return;
      try {
        const dependency = await taskApi.createTaskDependency({
          predecessor_task: predecessor.details.originalId,
          successor_task: successor.details.originalId,
          dependency_type: source.type,
        });
        setDependencies((prev) => [...prev, dependency]);
      } catch (err) {
        showTimelineError(err.message || "Failed to create dependency.");
      }
    }, [items, showTimelineError]);

    const setDependencyDraft = useCallback((key, value) => {
      setDependencyDrafts((prev) => ({ ...prev, [key]: value }));
    }, []);

    const openDependencyEditor = useCallback((event, itemId) => {
      event.preventDefault();
      event.stopPropagation();
      setSelectedId(itemId);
      setDependencyEditorId(itemId);
      setDependencyDrafts({
        predecessorTaskId: "",
        predecessorType: "FS",
        successorTaskId: "",
        successorType: "FS",
      });
    }, []);

    const addDependencyFromEditor = useCallback(
      async (relationship) => {
        const current = items[dependencyEditorId];
        if (!current || current.type !== "task") return;

        const draftTaskId =
          relationship === "predecessor"
            ? dependencyDrafts.predecessorTaskId
            : dependencyDrafts.successorTaskId;
        const type =
          relationship === "predecessor"
            ? dependencyDrafts.predecessorType
            : dependencyDrafts.successorType;
        const related = items[draftTaskId];

        if (!related || related.type !== "task" || related.id === current.id) {
          showTimelineError("Choose a different task for this dependency.");
          return;
        }

        const payload =
          relationship === "predecessor"
            ? {
                predecessor_task: related.details.originalId,
                successor_task: current.details.originalId,
                dependency_type: type,
              }
            : {
                predecessor_task: current.details.originalId,
                successor_task: related.details.originalId,
                dependency_type: type,
              };

        const existing = dependencyEdges.find(
          (edge) =>
            String(edge.predecessor_task) === String(payload.predecessor_task) &&
            String(edge.successor_task) === String(payload.successor_task),
        );
        if (existing) {
          showTimelineError("This dependency already exists. Change its type in the list.");
          return;
        }

        const savingKey = `${relationship}-add`;
        setDependencySavingKey(savingKey);
        try {
          const dependency = await taskApi.createTaskDependency(payload);
          setDependencies((prev) => [...prev, dependency]);
          setDependencyDrafts((prev) => ({
            ...prev,
            [`${relationship}TaskId`]: "",
            [`${relationship}Type`]: "FS",
          }));
          addAlert("Dependency added.", "success");
        } catch (err) {
          showTimelineError(err.message || "Failed to create dependency.");
        } finally {
          setDependencySavingKey(null);
        }
      },
      [
        addAlert,
        dependencyDrafts,
        dependencyEdges,
        dependencyEditorId,
        items,
        showTimelineError,
      ],
    );

    const changeDependencyType = useCallback(
      async (edge, nextType) => {
        if (!edge?.id || edge.dependency_type === nextType) return;
        const savingKey = `${edge.id}-type`;
        setDependencySavingKey(savingKey);
        try {
          let updated;
          try {
            updated = await taskApi.updateTaskDependency(edge.id, {
              dependency_type: nextType,
            });
          } catch (err) {
            const message = err?.message || "";
            if (!/405|method|not allowed|patch/i.test(message)) {
              throw err;
            }
            await taskApi.deleteTaskDependency(edge.id);
            try {
              updated = await taskApi.createTaskDependency({
                predecessor_task: edge.predecessor_task,
                successor_task: edge.successor_task,
                dependency_type: nextType,
              });
            } catch (createErr) {
              const restored = await taskApi.createTaskDependency({
                predecessor_task: edge.predecessor_task,
                successor_task: edge.successor_task,
                dependency_type: edge.dependency_type || "FS",
              });
              setDependencies((prev) =>
                prev.map((dependency) =>
                  dependencyKey(dependency) === dependencyKey(edge)
                    ? { ...dependency, ...restored }
                    : dependency,
                ),
              );
              throw createErr;
            }
          }
          setDependencies((prev) =>
            prev.map((dependency) =>
              dependencyKey(dependency) === dependencyKey(edge)
                ? { ...dependency, ...updated, dependency_type: nextType }
                : dependency,
            ),
          );
          addAlert("Dependency type updated.", "success");
        } catch (err) {
          showTimelineError(err.message || "Failed to update dependency type.");
        } finally {
          setDependencySavingKey(null);
        }
      },
      [addAlert, showTimelineError],
    );

    const removeDependencyFromEditor = useCallback(
      async (edge) => {
        if (!edge?.id) {
          showTimelineError("This dependency cannot be removed because it has no id.");
          return;
        }
        const savingKey = `${edge.id}-delete`;
        setDependencySavingKey(savingKey);
        try {
          await taskApi.deleteTaskDependency(edge.id);
          setDependencies((prev) =>
            prev.filter((dependency) => dependencyKey(dependency) !== dependencyKey(edge)),
          );
          addAlert("Dependency removed.", "success");
        } catch (err) {
          showTimelineError(err.message || "Failed to remove dependency.");
        } finally {
          setDependencySavingKey(null);
        }
      },
      [addAlert, showTimelineError],
    );
  
    const handleAnchorClick = (event, item, side) => {
      event.preventDefault();
      event.stopPropagation();
      if (!hasTaskAuthority || item.type !== "task") return;
      if (pendingDependency) {
        createDependency(item);
        return;
      }
      setPendingDependency({
        itemId: item.id,
        type: side === "right" ? "FS" : "SS",
      });
    };
  
    const beginDependencyDrag = (event, item, side) => {
      if (!hasTaskAuthority || item.type !== "task") return;
      const point = pointerToSvgPoint(event);
      if (!point) return;
      event.preventDefault();
      event.stopPropagation();
      const type = side === "right" ? "FS" : "SS";
      setPendingDependency({ itemId: item.id, type });
      setDependencyDrag({
        itemId: item.id,
        type,
        startX: point.x,
        startY: point.y,
        currentX: point.x,
        currentY: point.y,
      });
    };
  
    useEffect(() => {
      if (!dependencyDrag) return undefined;
      const onMove = (event) => {
        autoScrollTimeline(event);
        const point = pointerToSvgPoint(event);
        if (!point) return;
        setDependencyDrag((current) =>
          current
            ? { ...current, currentX: point.x, currentY: point.y }
            : current,
        );
      };
      const onUp = async (event) => {
        const point = pointerToSvgPoint(event);
        const target = point ? rowAtSvgY(point.y) : null;
        await createDependencyFrom(dependencyDrag, target);
        setDependencyDrag(null);
        setPendingDependency(null);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
      return () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
    }, [autoScrollTimeline, createDependencyFrom, dependencyDrag, pointerToSvgPoint, rowAtSvgY]);
  
    const dependencyViolation = (edge) => {
      const predecessor = items[edge.predecessorId];
      const successor = items[edge.successorId];
      if (!itemHasDates(predecessor) || !itemHasDates(successor)) return false;
      if (edge.dependency_type === "SS") return successor.start < predecessor.start;
      return successor.start < predecessor.end;
    };
  
    const centerOnDate = useCallback((date) => {
      const right = rightRef.current;
      if (!right || !date) return;
      right.scrollLeft = clamp(
        xForDate(date) - right.clientWidth / 2,
        0,
        Math.max(0, right.scrollWidth - right.clientWidth),
      );
      updateTimelineViewport();
    }, [updateTimelineViewport, xForDate]);

    const revealVisibleSchedule = useCallback(() => {
      const right = rightRef.current;
      if (!right || dragState || placementDrag || dependencyDrag) return;
      if (right.scrollTop === lastRevealScrollTopRef.current) return;
      lastRevealScrollTopRef.current = right.scrollTop;

      const visibleTop = Math.max(0, right.scrollTop - HEADER_HEIGHT);
      const visibleBottom = visibleTop + Math.max(0, right.clientHeight - HEADER_HEIGHT);
      const visibleDatedRows = rows.filter(
        (row) =>
          row.type !== "add" &&
          itemHasDates(row) &&
          row.top + row.height > visibleTop &&
          row.top < visibleBottom,
      );
      if (!visibleDatedRows.length) return;

      const viewLeft = right.scrollLeft + 24;
      const viewRight = right.scrollLeft + right.clientWidth - 24;
      const intersectsView = (row) => {
        const x = xForDate(row.start);
        const w = widthForSpan(row.start, row.end);
        return x + w >= viewLeft && x <= viewRight;
      };

      const target =
        visibleDatedRows.find((row) => row.id === selectedId && !intersectsView(row)) ||
        (!visibleDatedRows.some(intersectsView) ? visibleDatedRows[0] : null);

      if (!target) return;
      right.scrollLeft = clamp(
        xForDate(target.start) - 48,
        0,
        right.scrollWidth - right.clientWidth,
      );
    }, [
      dependencyDrag,
      dragState,
      placementDrag,
      rows,
      selectedId,
      widthForSpan,
      xForDate,
    ]);
  
    const fitToContent = useCallback(() => {
      setTimeout(() => centerOnDate(new Date()), 0);
    }, [centerOnDate]);
  
    useEffect(() => {
      if (!loading && rows.length && !didAutoFitRef.current) {
        fitToContent();
        didAutoFitRef.current = true;
      }
    }, [fitToContent, loading, rows.length]);
  
    useEffect(() => {
      const right = rightRef.current;
      if (!right) return undefined;
      right.addEventListener("scroll", updateTimelineViewport);
      right.addEventListener("scroll", revealVisibleSchedule);
      const resizeObserver =
        typeof ResizeObserver === "undefined"
          ? null
          : new ResizeObserver(updateTimelineViewport);
      resizeObserver?.observe(right);
      updateTimelineViewport();
      return () => {
        right.removeEventListener("scroll", updateTimelineViewport);
        right.removeEventListener("scroll", revealVisibleSchedule);
        resizeObserver?.disconnect();
      };
    }, [revealVisibleSchedule, updateTimelineViewport]);
  
    const renderSidebarValue = (row, key) => {
      if (key === "start") return row.start ? formatISODate(row.start) : "";
      if (key === "end") return row.end ? formatISODate(row.end) : "";
      if (key === "status") {
        return (
          <span
            className={clsx(
              "inline-flex max-w-full items-center rounded-md px-2 py-1 text-xs",
              statusClass(row.status),
            )}
            title={row.status || "No status"}
          >
            <span className="truncate">{row.status || "No status"}</span>
          </span>
        );
      }
      return null;
    };

    const dependencyEditorRow = dependencyEditorId ? rowMap[dependencyEditorId] : null;
    const dependencyEditorStyle = (() => {
      if (!dependencyEditorRow || !itemHasDates(dependencyEditorRow)) return null;
      const right = rightRef.current;
      const viewportLeft = right?.scrollLeft || 0;
      const viewportTop = right?.scrollTop || 0;
      const viewportWidth = right?.clientWidth || 520;
      const viewportHeight = right?.clientHeight || 520;
      const width = 450;
      const height = 390;
      const desiredLeft =
        xForDate(dependencyEditorRow.start) +
        widthForSpan(dependencyEditorRow.start, dependencyEditorRow.end) +
        38;
      const desiredTop = HEADER_HEIGHT + dependencyEditorRow.top - 10;

      return {
        left: clamp(
          desiredLeft,
          viewportLeft + 12,
          Math.max(viewportLeft + 12, viewportLeft + viewportWidth - width - 12),
        ),
        top: clamp(
          desiredTop,
          viewportTop + 12,
          Math.max(viewportTop + 12, viewportTop + viewportHeight - height - 12),
        ),
        width,
      };
    })();

    const renderDependencySection = (
      relationship,
      label,
      Icon,
      edges,
      emptyText,
    ) => {
      const taskKey = `${relationship}TaskId`;
      const typeKey = `${relationship}Type`;
      const excludedIds = new Set([
        dependencyEditorId,
        ...edges.map((edge) =>
          relationship === "predecessor" ? edge.predecessorId : edge.successorId,
        ),
      ]);
      const options = taskOptions.filter((task) => !excludedIds.has(task.id));
      const addKey = `${relationship}-add`;

      return (
        <section className="space-y-2">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-slate-900" />
            <h3 className="text-sm font-semibold text-slate-900">{label}</h3>
            <Info className="h-3.5 w-3.5 text-slate-500" />
          </div>

          <div className="grid grid-cols-[minmax(0,1fr)_72px_64px] gap-2 text-[11px] font-semibold uppercase text-slate-500">
            <span>Task</span>
            <span>Type</span>
            <span />
          </div>

          {edges.length ? (
            edges.map((edge) => {
              const related =
                relationship === "predecessor"
                  ? items[edge.predecessorId]
                  : items[edge.successorId];
              const savingType = dependencySavingKey === `${edge.id}-type`;
              const savingDelete = dependencySavingKey === `${edge.id}-delete`;
              return (
                <div
                  key={`${relationship}-${dependencyKey(edge)}`}
                  className="grid grid-cols-[minmax(0,1fr)_72px_64px] items-center gap-2"
                >
                  <div
                    className="h-8 truncate rounded border border-cyan-300 bg-cyan-100 px-2 py-1.5 text-sm font-medium text-slate-900"
                    title={related?.label || "Task"}
                  >
                    {related?.label || "Task"}
                  </div>
                  <select
                    value={edge.dependency_type || "FS"}
                    disabled={Boolean(dependencySavingKey)}
                    onChange={(event) => changeDependencyType(edge, event.target.value)}
                    className="h-8 rounded border border-slate-200 bg-blue-50 px-1 text-sm font-semibold text-blue-700 outline-none focus:border-blue-500"
                    title={savingType ? "Updating type" : "Dependency type"}
                  >
                    <option value="FS">FS</option>
                    <option value="SS">SS</option>
                  </select>
                  <button
                    type="button"
                    disabled={Boolean(dependencySavingKey)}
                    onClick={() => removeDependencyFromEditor(edge)}
                    className="inline-flex h-8 items-center justify-center rounded text-slate-500 hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
                    title={savingDelete ? "Removing" : "Remove dependency"}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              );
            })
          ) : (
            <div className="rounded bg-slate-50 px-3 py-3 text-center text-sm font-medium text-slate-500">
              {emptyText}
            </div>
          )}

          {hasTaskAuthority && (
            <div className="grid grid-cols-[minmax(0,1fr)_72px_64px] items-center gap-2">
              <select
                value={dependencyDrafts[taskKey]}
                onChange={(event) => setDependencyDraft(taskKey, event.target.value)}
                className="h-8 min-w-0 rounded border border-slate-200 bg-white px-2 text-sm outline-none focus:border-blue-500"
              >
                <option value="">Select task</option>
                {options.map((task) => (
                  <option key={`${relationship}-option-${task.id}`} value={task.id}>
                    {task.label}
                  </option>
                ))}
              </select>
              <select
                value={dependencyDrafts[typeKey]}
                onChange={(event) => setDependencyDraft(typeKey, event.target.value)}
                className="h-8 rounded border border-slate-200 bg-white px-1 text-sm font-semibold text-slate-700 outline-none focus:border-blue-500"
              >
                <option value="FS">FS</option>
                <option value="SS">SS</option>
              </select>
              <button
                type="button"
                disabled={!dependencyDrafts[taskKey] || Boolean(dependencySavingKey)}
                onClick={() => addDependencyFromEditor(relationship)}
                className="h-8 rounded bg-blue-50 px-2 text-sm font-semibold text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
              >
                {dependencySavingKey === addKey ? "..." : "Add"}
              </button>
            </div>
          )}
        </section>
      );
    };
  
    const activeDateLabelRow = hoverBarId ? rowMap[hoverBarId] : null;
    const movingRow = dragState ? rowMap[dragState.id] : null;
    const rowScrollControls = useMemo(() => {
      const { left, width } = timelineViewport;
      if (!width) return [];

      const edgeGap = 10;
      const controlSize = 32;
      const viewLeft = left + edgeGap;
      const viewRight = left + width - edgeGap;

      return rows.reduce((controls, row) => {
        if (row.type === "add" || !itemHasDates(row)) return controls;

        const x = xForDate(row.start);
        const w = widthForSpan(row.start, row.end);
        const isOffLeft = x + w < viewLeft;
        const isOffRight = x > viewRight;
        if (!isOffLeft && !isOffRight) return controls;

        const targetDate = isOffLeft ? row.end : row.start;
        controls.push({
          id: row.id,
          side: isOffLeft ? "left" : "right",
          targetDate,
          x: isOffLeft ? left + edgeGap : left + width - edgeGap - controlSize,
          y: row.top + Math.max(0, (row.height - controlSize) / 2),
          label: `Scroll to: ${targetDate.toLocaleDateString(undefined, {
            weekday: "short",
            month: "short",
            day: "numeric",
          })}`,
        });
        return controls;
      }, []);
    }, [rows, timelineViewport, widthForSpan, xForDate]);
  
    return (
      <div className="relative isolate z-0 flex h-full min-h-0 w-full flex-col px-4 pb-3">
        <div className="flex h-12 flex-shrink-0 items-center gap-2 border-b border-slate-200">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <CalendarDays className="h-4 w-4 text-[#3B0069]" />
            Task Timeline
          </div>
          {pendingDependency && (
            <div className="ml-2 inline-flex items-center gap-2 rounded-md bg-[#3B0069]/10 px-2 py-1 text-xs text-[#3B0069]">
              <Link2 className="h-3.5 w-3.5" />
              {pendingDependency.type}: choose successor task
            </div>
          )}
          <div className="ml-auto flex items-center gap-1.5">
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded border border-slate-200 text-slate-700 hover:bg-slate-50"
              onClick={() => setPxPerDay((value) => clamp(value - 2, MIN_PX_PER_DAY, MAX_PX_PER_DAY))}
              title="Zoom out"
            >
              <ZoomOut className="h-4 w-4" />
            </button>
            {[
              ["Day", DAY_PX_PER_DAY],
              ["Week", 10],
              ["Month", 5],
            ].map(([label, value]) => (
              <button
                key={label}
                type="button"
                className={clsx(
                  "h-8 rounded border px-3 text-xs",
                  pxPerDay === value
                    ? "border-[#3B0069] bg-[#3B0069] text-white"
                    : "border-slate-200 text-slate-700 hover:bg-slate-50",
                )}
                onClick={() => setPxPerDay(value)}
              >
                {label}
              </button>
            ))}
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded border border-slate-200 text-slate-700 hover:bg-slate-50"
              onClick={() => setPxPerDay((value) => clamp(value + 2, MIN_PX_PER_DAY, MAX_PX_PER_DAY))}
              title="Zoom in"
            >
              <ZoomIn className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={clsx(
                "inline-flex h-8 items-center gap-1.5 rounded border px-2 text-xs",
                showCriticalPath
                  ? "border-rose-500 bg-rose-50 text-rose-700"
                  : "border-slate-200 text-slate-700 hover:bg-slate-50",
              )}
              onClick={() => setShowCriticalPath((value) => !value)}
            >
              <Route className="h-4 w-4" />
              Critical
            </button>
            <div className="relative">
              <button
                type="button"
                className="inline-flex h-8 items-center gap-1.5 rounded border border-slate-200 px-2 text-xs text-slate-700 hover:bg-slate-50"
                onClick={() => setShowColumns((value) => !value)}
              >
                <Columns3 className="h-4 w-4" />
                Columns
              </button>
              {showColumns && (
                <div className="absolute right-0 top-9 z-30 w-44 rounded border border-slate-200 bg-white p-2 shadow-lg">
                  {Object.entries(columnCatalog).map(([key, column]) => (
                    <label
                      key={key}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                    >
                      <input
                        type="checkbox"
                        checked={visibleColumns[key]}
                        onChange={() =>
                          setVisibleColumns((prev) => ({
                            ...prev,
                            [key]: !prev[key],
                          }))
                        }
                      />
                      {column.label}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="relative">
              <button
                type="button"
                className="inline-flex h-8 items-center gap-1.5 rounded border border-slate-200 px-2 text-xs text-slate-700 hover:bg-slate-50"
                onClick={() => setShowColors((value) => !value)}
              >
                <Palette className="h-4 w-4" />
                Colors
              </button>
              {showColors && selectedId && items[selectedId] && (
                <div className="absolute right-0 top-9 z-30 w-56 rounded border border-slate-200 bg-white p-3 shadow-lg">
                  <div className="mb-2 truncate text-xs font-medium text-slate-600">
                    {items[selectedId].label}
                  </div>
                  <input
                    type="color"
                    className="h-9 w-full cursor-pointer"
                    value={itemColor(items[selectedId])}
                    onChange={(event) =>
                      setCustomColors((prev) => ({
                        ...prev,
                        [selectedId]: event.target.value,
                      }))
                    }
                  />
                </div>
              )}
            </div>
            <button
              type="button"
              className="inline-flex h-8 items-center gap-1.5 rounded border border-slate-200 px-2 text-xs text-slate-700 hover:bg-slate-50"
              onClick={fitToContent}
            >
              <Focus className="h-4 w-4" />
              Today
            </button>
            <button
              type="button"
              className="inline-flex h-8 items-center gap-1.5 rounded border border-slate-200 px-2 text-xs text-slate-700 hover:bg-slate-50"
              onClick={loadData}
            >
              <RefreshCw className="h-4 w-4" />
              Reload
            </button>
          </div>
        </div>
  
        <div
          className="grid min-h-0 flex-1 overflow-hidden border border-t-0 border-slate-200"
          style={{ gridTemplateColumns: `${leftWidth}px minmax(0, 1fr)` }}
        >
          <div ref={leftRef} className="min-h-0 overflow-auto border-r border-slate-200 bg-white">
            <div
              className="sticky top-0 z-20 grid border-b border-slate-200 bg-slate-50 text-[10px] font-semibold uppercase tracking-normal text-slate-500"
              style={{
                height: HEADER_HEIGHT,
                gridTemplateColumns: sidebarGridColumns,
              }}
            >
              <div className="flex items-center border-r border-slate-200 px-3">Task Structure</div>
              <div className="flex items-center justify-center border-r border-slate-200 px-2 text-center">Assignees</div>
              <div className="flex items-center justify-center border-r border-slate-200 px-2 text-center">Duration</div>
              <div className="flex items-center justify-center border-r border-slate-200 px-2 text-center">Progress</div>
              {visibleSidebarColumns.map((column) => (
                <div key={column.key} className="flex items-center justify-center border-r border-slate-200 px-2 text-center">
                  {column.label}
                </div>
              ))}
            </div>
            <div className="relative" style={{ height: bodyHeight }}>
              {rows.map((row) => {
                if (row.type === "add") {
                  const isActiveAddRow = hoverAddRow === row.id || addDraft?.rowId === row.id;
                  return (
                    <div
                      key={row.id}
                      className={clsx(
                        "grid text-sm text-slate-700 transition-colors",
                        isActiveAddRow && "border-y border-blue-500 bg-blue-50/80",
                      )}
                      style={{
                        height: row.height,
                        gridTemplateColumns: sidebarGridColumns,
                      }}
                      onMouseEnter={() => setHoverAddRow(row.id)}
                      onMouseLeave={() => setHoverAddRow(null)}
                    >
                      <div
                        className="flex min-w-0 items-center gap-2 border-r border-slate-100 px-3"
                        style={{ gridColumn: "1 / span 4" }}
                      >
                        {!addDraft || addDraft.rowId !== row.id ? (
                          <div
                            className={clsx(
                              "flex min-w-0 items-center gap-2 transition-opacity",
                              isActiveAddRow
                                ? "opacity-100"
                                : "pointer-events-none opacity-0",
                            )}
                          >
                            <button
                              type="button"
                              className="inline-flex h-6 w-6 items-center justify-center rounded text-blue-600 hover:bg-blue-100"
                              onClick={() => startAddDraft("task", row.groupId, row.id)}
                              title="Add new task"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              className="inline-flex h-6 w-6 items-center justify-center rounded text-blue-600 hover:bg-blue-100"
                              onClick={() => startAddDraft("group", row.groupId, row.id)}
                              title="Add new task group"
                            >
                              <FolderPlus className="h-4 w-4" />
                            </button>
                            <span className="text-xs text-slate-500">
                              Add task or task group
                            </span>
                          </div>
                        ) : (
                          <form
                            className="flex min-w-0 flex-1 items-center gap-2"
                            onSubmit={submitAddDraft}
                          >
                            {addDraft.type === "task" ? (
                              <Plus className="h-4 w-4 flex-shrink-0 text-blue-600" />
                            ) : (
                              <FolderPlus className="h-4 w-4 flex-shrink-0 text-blue-600" />
                            )}
                            <input
                              ref={addInputRef}
                              value={addName}
                              onChange={(event) => setAddName(event.target.value)}
                              onKeyDown={(event) => {
                                if (event.key === "Escape") cancelAddDraft();
                              }}
                              placeholder={
                                addDraft.type === "task" ? "Add task" : "Add group"
                              }
                              disabled={addSubmitting}
                              className="h-6 min-w-0 flex-1 rounded border border-slate-300 bg-white px-2 text-sm font-medium outline-none focus:border-blue-500"
                            />
                            <button
                              type="submit"
                              disabled={addSubmitting || !addName.trim()}
                              className="h-6 rounded bg-blue-600 px-2 text-xs font-medium text-white disabled:cursor-not-allowed disabled:bg-slate-300"
                            >
                              Add
                            </button>
                            <button
                              type="button"
                              className="h-6 rounded px-2 text-xs text-slate-500 hover:bg-slate-100"
                              onClick={cancelAddDraft}
                              disabled={addSubmitting}
                            >
                              Cancel
                            </button>
                          </form>
                        )}
                      </div>
                      {visibleSidebarColumns.map((column) => (
                        <div
                          key={`add-${row.id}-${column.key}`}
                          className="border-r border-slate-100"
                        />
                      ))}
                    </div>
                  );
                }

                const assignees = resolveAssignees(row);
                return (
                  <div
                    key={row.id}
                    className={clsx(
                      "grid border-b border-slate-100 text-sm text-slate-700",
                      selectedId === row.id && "bg-[#3B0069]/5",
                    )}
                    style={{
                      height: row.height,
                      gridTemplateColumns: sidebarGridColumns,
                    }}
                    onClick={() => setSelectedId(row.id)}
                  >
                    <div className="flex min-w-0 items-center gap-2 border-r border-slate-100 px-3">
                      <span style={{ width: row.level * 18 }} className="flex-shrink-0" />
                      {row.type !== "subtask" ? (
                        <button
                          type="button"
                          className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded hover:bg-slate-100"
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleExpand(row.id);
                          }}
                        >
                          {row.expanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                      ) : (
                        <span className="h-6 w-6 flex-shrink-0" />
                      )}
                      <span
                        className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                        style={{ backgroundColor: itemColor(row) }}
                      />
                      <span className="truncate font-medium" title={row.label}>
                        {row.label}
                      </span>
                    </div>
                    <div className="flex items-center justify-center border-r border-slate-100 px-2">
                      <div className="flex -space-x-2">
                        {assignees.slice(0, 3).map((member, index) =>
                          getAvatarUrl(member) ? (
                            <img
                              key={`${row.id}-avatar-${index}`}
                              src={getAvatarUrl(member)}
                              alt={getMemberName(member)}
                              className="h-6 w-6 rounded-full border border-white object-cover"
                            />
                          ) : (
                            <span
                              key={`${row.id}-avatar-${index}`}
                              className="flex h-6 w-6 items-center justify-center rounded-full border border-white bg-slate-200 text-[10px] font-semibold text-slate-600"
                              title={getMemberName(member)}
                            >
                              {memberInitials(member)}
                            </span>
                          ),
                        )}
                        {assignees.length > 3 && (
                          <span className="flex h-6 w-6 items-center justify-center rounded-full border border-white bg-slate-900 text-[10px] font-semibold text-white">
                            +{assignees.length - 3}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-center border-r border-slate-100 px-2 text-xs">
                      {itemHasDates(row) ? `${daysBetween(row.start, row.end)}d` : ""}
                    </div>
                    <div className="flex items-center justify-center gap-1.5 border-r border-slate-100 px-2 text-xs">
                      <div className="h-1.5 w-5 overflow-hidden rounded-full bg-slate-100">
                        <div
                          className="h-full rounded-full bg-[#3B0069]"
                          style={{ width: `${clamp(row.progress || 0, 0, 100)}%` }}
                        />
                      </div>
                      {Math.round(row.progress || 0)}%
                    </div>
                    {visibleSidebarColumns.map((column) => (
                      <div key={column.key} className="flex min-w-0 items-center justify-center border-r border-slate-100 px-2 text-center text-xs">
                        <span className="truncate">{renderSidebarValue(row, column.key)}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
  
          <div ref={rightRef} className="relative min-h-0 overflow-auto bg-white">
            <div
              className="sticky top-0 z-20 border-b border-slate-200 bg-white"
              style={{ height: HEADER_HEIGHT, width: contentWidth }}
            >
              <div className="relative h-full">
                {timelineScale === "month"
                  ? quarterTicks.map((date, index) => {
                      const next = quarterTicks[index + 1] || addDays(maxDate, 1);
                      return (
                        <div
                          key={`quarter-${formatISODate(date)}`}
                          className="absolute top-0 flex h-6 items-center justify-center border-r border-slate-900 text-[13px] font-semibold text-slate-900"
                          style={{
                            left: xForDate(date),
                            width: Math.max(56, xForDate(next) - xForDate(date)),
                          }}
                        >
                          {quarterLabel(date)}
                        </div>
                      );
                    })
                  : monthTicks.map((date, index) => {
                      const next = monthTicks[index + 1] || addDays(maxDate, 1);
                      return (
                        <div
                          key={formatISODate(date)}
                          className="absolute top-0 flex h-6 items-center justify-center border-r border-slate-900 text-[13px] font-semibold text-slate-900"
                          style={{
                            left: xForDate(date),
                            width: Math.max(40, xForDate(next) - xForDate(date)),
                          }}
                        >
                          {date.toLocaleString(undefined, {
                            month: "short",
                            year: "numeric",
                          })}
                        </div>
                      );
                    })}
                {timelineScale === "day" &&
                  dayTicks.map((date) => {
                    const isMonthBoundary = addDays(date, 1).getDate() === 1;
                    return (
                      <div
                        key={`tick-${formatISODate(date)}`}
                        className={clsx(
                          "absolute bottom-0 flex h-8 flex-col items-center justify-start border-r text-[11px] leading-none",
                          isMonthBoundary
                            ? "border-r-slate-900"
                            : "border-r-slate-400",
                          date.getDate() === 1 ? "text-slate-900" : "text-slate-700",
                        )}
                        style={{ left: xForDate(date), width: pxPerDay }}
                      >
                        <span className="mt-0.5 font-semibold">{date.getDate()}</span>
                        <span className="mt-1 text-[10px] font-medium text-slate-500">
                          {WEEKDAY_LABELS[date.getDay()]}
                        </span>
                      </div>
                    );
                  })}
                {timelineScale === "week" &&
                  weekTicks.map((date) => (
                    <div
                      key={`week-${formatISODate(date)}`}
                      className="absolute bottom-0 flex h-8 items-start justify-center border-r border-r-slate-300 text-[13px] font-semibold text-slate-900"
                      style={{
                        left: xForDate(date),
                        width: 7 * pxPerDay,
                      }}
                    >
                      <span className="mt-1">{date.getDate()}</span>
                    </div>
                  ))}
                {timelineScale === "month" &&
                  monthTicks.map((date, index) => {
                    const next = monthTicks[index + 1] || addDays(maxDate, 1);
                    return (
                      <div
                        key={`month-${formatISODate(date)}`}
                        className="absolute bottom-0 flex h-8 items-start border-r border-r-slate-400 px-1.5 text-[13px] font-semibold text-slate-900"
                        style={{
                          left: xForDate(date),
                          width: Math.max(48, xForDate(next) - xForDate(date)),
                        }}
                      >
                        <span className="mt-1">
                          {date.toLocaleString(undefined, { month: "short" })}
                        </span>
                      </div>
                    );
                  })}
                {timelineScale === "week" &&
                  monthTicks.map((date) => (
                    <div
                      key={`header-week-month-line-${formatISODate(date)}`}
                      className="pointer-events-none absolute top-0 h-full w-px bg-slate-900"
                      style={{ left: xForDate(date) }}
                    />
                  ))}
                {timelineScale === "month" &&
                  quarterTicks.map((date) => (
                    <div
                      key={`header-quarter-line-${formatISODate(date)}`}
                      className="pointer-events-none absolute top-0 h-full w-px bg-slate-900"
                      style={{ left: xForDate(date) }}
                    />
                  ))}
              </div>
            </div>
  
            <svg
              ref={svgRef}
              width={contentWidth}
              height={bodyHeight}
              className="block"
              onMouseLeave={() => setHoverPlacement(null)}
            >
              <defs>
                <marker
                  id="gantt-arrow"
                  markerWidth="9"
                  markerHeight="9"
                  refX="8"
                  refY="4.5"
                  orient="auto"
                >
                  <path d="M0,0 L9,4.5 L0,9 z" fill="#3B0069" />
                </marker>
                <marker
                  id="gantt-arrow-red"
                  markerWidth="9"
                  markerHeight="9"
                  refX="8"
                  refY="4.5"
                  orient="auto"
                >
                  <path d="M0,0 L9,4.5 L0,9 z" fill="#E11D48" />
                </marker>
                <marker
                  id="gantt-arrow-orange"
                  markerWidth="9"
                  markerHeight="9"
                  refX="8"
                  refY="4.5"
                  orient="auto"
                >
                  <path d="M0,0 L9,4.5 L0,9 z" fill="#F97316" />
                </marker>
              </defs>
  
              {rows.map((row) => {
                if (row.type === "add") {
                  const isActiveAddRow = hoverAddRow === row.id || addDraft?.rowId === row.id;
                  return (
                    <g key={`grid-${row.id}`}>
                      <rect
                        x={0}
                        y={row.top}
                        width={contentWidth}
                        height={row.height}
                        fill={isActiveAddRow ? "#EFF6FF" : "transparent"}
                        opacity={0.8}
                        onMouseEnter={() => setHoverAddRow(row.id)}
                        onMouseLeave={() => setHoverAddRow(null)}
                      />
                      {isActiveAddRow && (
                        <>
                          <line
                            x1={0}
                            x2={contentWidth}
                            y1={row.top + 0.5}
                            y2={row.top + 0.5}
                            stroke="#3B82F6"
                            strokeWidth={1.2}
                          />
                          <line
                            x1={0}
                            x2={contentWidth}
                            y1={row.top + row.height - 0.5}
                            y2={row.top + row.height - 0.5}
                            stroke="#3B82F6"
                            strokeWidth={1.2}
                          />
                        </>
                      )}
                    </g>
                  );
                }
                return (
                  <g key={`grid-${row.id}`}>
                    <rect
                      x={0}
                      y={row.top}
                      width={contentWidth}
                      height={row.height}
                      fill={row.id === hoverPlacement?.id ? "#F8FAFC" : "transparent"}
                      onMouseMove={(event) => handleTimelineMove(event, row)}
                      onMouseDown={(event) => beginPlacement(event, row)}
                      onMouseUp={finishPlacement}
                      onClick={(event) => handlePlacementClick(event, row)}
                    />
                    <line
                      x1={0}
                      x2={contentWidth}
                      y1={row.top + row.height - 0.5}
                      y2={row.top + row.height - 0.5}
                      stroke="#E2E8F0"
                    />
                  </g>
                );
              })}
  
              {timelineScale === "day" &&
                dayTicks.map((date) => (
                  <line
                    key={`body-tick-${formatISODate(date)}`}
                    x1={xForDate(date)}
                    x2={xForDate(date)}
                    y1={0}
                    y2={bodyHeight}
                    stroke={date.getDate() === 1 ? "#111827" : "#CBD5E1"}
                    strokeWidth={date.getDate() === 1 ? 1.4 : 0.8}
                  />
                ))}

              {timelineScale === "week" && (
                <>
                  {weekTicks.map((date) => (
                    <line
                      key={`body-week-${formatISODate(date)}`}
                      x1={xForDate(date)}
                      x2={xForDate(date)}
                      y1={0}
                      y2={bodyHeight}
                      stroke="#CBD5E1"
                      strokeWidth={0.8}
                    />
                  ))}
                  {monthTicks.map((date) => (
                    <line
                      key={`body-week-month-${formatISODate(date)}`}
                      x1={xForDate(date)}
                      x2={xForDate(date)}
                      y1={0}
                      y2={bodyHeight}
                      stroke="#111827"
                      strokeWidth={1.3}
                    />
                  ))}
                </>
              )}

              {timelineScale === "month" && (
                <>
                  {monthTicks.map((date) => (
                    <line
                      key={`body-month-${formatISODate(date)}`}
                      x1={xForDate(date)}
                      x2={xForDate(date)}
                      y1={0}
                      y2={bodyHeight}
                      stroke="#CBD5E1"
                      strokeWidth={0.9}
                    />
                  ))}
                  {quarterTicks.map((date) => (
                    <line
                      key={`body-quarter-${formatISODate(date)}`}
                      x1={xForDate(date)}
                      x2={xForDate(date)}
                      y1={0}
                      y2={bodyHeight}
                      stroke="#111827"
                      strokeWidth={1.2}
                    />
                  ))}
                </>
              )}

              <line
                x1={0}
                x2={contentWidth}
                y1={0}
                y2={0}
                stroke="#111827"
                strokeWidth={1.2}
              />
  
              {(() => {
                const today = new Date();
                const current = new Date(
                  today.getFullYear(),
                  today.getMonth(),
                  today.getDate(),
                );
                if (current < minDate || current > maxDate) return null;
                const x = xForDate(current);
                return (
                  <line
                    x1={x}
                    x2={x}
                    y1={0}
                    y2={bodyHeight}
                    stroke="#111827"
                    strokeDasharray="4 4"
                  />
                );
              })()}
  
              {dependencyEdges.map((edge) => {
                const predecessor = rowMap[edge.predecessorId];
                const successor = rowMap[edge.successorId];
                if (!predecessor || !successor || !itemHasDates(predecessor) || !itemHasDates(successor)) {
                  return null;
                }
                const isSS = edge.dependency_type === "SS";
                const startX = isSS
                  ? xForDate(predecessor.start)
                  : xForDate(predecessor.end) + widthForSpan(predecessor.end, predecessor.end);
                const endX = xForDate(successor.start);
                const startY = predecessor.top + ROW_HEIGHT / 2;
                const endY = successor.top + ROW_HEIGHT / 2;
                const midX = Math.max(startX + 22, Math.min(endX - 22, startX + 60));
                const path = `M ${startX} ${startY} L ${midX} ${startY} L ${midX} ${endY} L ${endX} ${endY}`;
                const violation = dependencyViolation(edge);
                const critical = criticalEdgeSet.has(dependencyKey(edge));
                return (
                  <path
                    key={`dep-${dependencyKey(edge)}`}
                    d={path}
                    fill="none"
                    stroke={violation ? "#E11D48" : critical ? "#F97316" : "#3B0069"}
                    strokeWidth={critical ? 2.5 : 1.7}
                    markerEnd={
                      violation
                        ? "url(#gantt-arrow-red)"
                        : critical
                          ? "url(#gantt-arrow-orange)"
                          : "url(#gantt-arrow)"
                    }
                    opacity={critical || violation ? 1 : 0.72}
                  />
                );
              })}
  
              {dependencyDrag && (
                <g pointerEvents="none">
                  <path
                    d={`M ${dependencyDrag.startX} ${dependencyDrag.startY} L ${dependencyDrag.currentX} ${dependencyDrag.currentY}`}
                    fill="none"
                    stroke="#2563EB"
                    strokeWidth={2.5}
                    strokeDasharray="6 4"
                    markerEnd="url(#gantt-arrow)"
                  />
                  <circle
                    cx={dependencyDrag.currentX}
                    cy={dependencyDrag.currentY}
                    r={5}
                    fill="#2563EB"
                  />
                </g>
              )}
  
              {movingRow && itemHasDates(movingRow) && (
                <rect
                  x={xForDate(movingRow.start)}
                  y={movingRow.top}
                  width={widthForSpan(movingRow.start, movingRow.end)}
                  height={ROW_HEIGHT}
                  fill={itemColor(movingRow)}
                  opacity={0.12}
                />
              )}
  
              {hoverPlacement && rowMap[hoverPlacement.id] && !itemHasDates(rowMap[hoverPlacement.id]) && (
                <g pointerEvents="none">
                  <rect
                    x={hoverPlacement.x}
                    y={rowMap[hoverPlacement.id].top + 9}
                    width={hoverPlacement.width}
                    height={ROW_HEIGHT - 18}
                    rx={5}
                    fill={itemColor(rowMap[hoverPlacement.id])}
                    opacity={0.24}
                  />
                </g>
              )}
  
              {placementDrag && rowMap[placementDrag.id] && (
                <g pointerEvents="none">
                  {(() => {
                    const row = rowMap[placementDrag.id];
                    const start =
                      placementDrag.start <= placementDrag.end
                        ? placementDrag.start
                        : placementDrag.end;
                    const end =
                      placementDrag.start <= placementDrag.end
                        ? placementDrag.end
                        : placementDrag.start;
                    const x = xForDate(start);
                    const w = widthForSpan(start, end);
                    return (
                      <>
                        <rect
                          x={x}
                          y={row.top}
                          width={w}
                          height={ROW_HEIGHT}
                          fill={itemColor(row)}
                          opacity={0.12}
                        />
                        <rect
                          x={x}
                          y={row.top + 9}
                          width={w}
                          height={ROW_HEIGHT - 18}
                          rx={5}
                          fill={itemColor(row)}
                          opacity={0.48}
                          stroke={itemColor(row)}
                          strokeWidth={1.5}
                        />
                      </>
                    );
                  })()}
                </g>
              )}
  
              {rows.map((row) => {
                if (!itemHasDates(row)) return null;
                const x = xForDate(row.start);
                const w = widthForSpan(row.start, row.end);
                const y = row.top + 9;
                const h = ROW_HEIGHT - 18;
                const color = itemColor(row);
                const critical = criticalNodeSet.has(row.id);
                const progressWidth = (w * clamp(row.progress || 0, 0, 100)) / 100;
                return (
                  <g
                    key={`bar-${row.id}`}
                    onMouseEnter={() => setHoverBarId(row.id)}
                    onMouseLeave={() => setHoverBarId(null)}
                  >
                    <rect
                      x={x}
                      y={y}
                      width={w}
                      height={h}
                      rx={5}
                      fill={color}
                      opacity={row.type === "group" ? 0.24 : 0.18}
                      stroke={critical ? "#F97316" : "transparent"}
                      strokeWidth={critical ? 2 : 0}
                    />
                    <rect
                      x={x}
                      y={y}
                      width={progressWidth}
                      height={h}
                      rx={5}
                      fill={color}
                      opacity={0.45}
                    />
                    <rect
                      x={x}
                      y={y}
                      width={w}
                      height={h}
                      rx={5}
                      fill={color}
                      opacity={row.type === "group" ? 0.65 : 0.85}
                      className={hasTaskAuthority ? "cursor-grab" : ""}
                      onMouseDown={(event) => beginDrag(event, row.id, "move")}
                      onClick={() => setSelectedId(row.id)}
                    />
                    <foreignObject
                      x={x + 8}
                      y={y}
                      width={Math.max(0, w - 16)}
                      height={h}
                      style={{ pointerEvents: "none" }}
                    >
                      <div
                        xmlns="http://www.w3.org/1999/xhtml"
                        className="flex h-full items-center overflow-hidden text-xs font-medium text-white"
                        style={{ pointerEvents: "none" }}
                      >
                        <span className="truncate">{row.label}</span>
                      </div>
                    </foreignObject>
                    {hasTaskAuthority && row.type !== "group" && (
                      <>
                        <rect
                          x={x - 4}
                          y={y}
                          width={8}
                          height={h}
                          rx={4}
                          fill="#FFFFFF"
                          opacity={0.75}
                          className="cursor-ew-resize"
                          onMouseDown={(event) => beginDrag(event, row.id, "resize-left")}
                        />
                        <rect
                          x={x + w - 4}
                          y={y}
                          width={8}
                          height={h}
                          rx={4}
                          fill="#FFFFFF"
                          opacity={0.75}
                          className="cursor-ew-resize"
                          onMouseDown={(event) => beginDrag(event, row.id, "resize-right")}
                        />
                      </>
                    )}
                    {row.type === "task" && (
                      <>
                        <circle
                          cx={x - 10}
                          cy={y + h / 2}
                          r={6}
                          fill={pendingDependency ? "#3B0069" : "#FFFFFF"}
                          stroke="#3B0069"
                          strokeWidth={2}
                          className="cursor-crosshair"
                          onMouseDown={(event) =>
                            beginDependencyDrag(event, row, "left")
                          }
                          onClick={(event) => handleAnchorClick(event, row, "left")}
                        />
                        <circle
                          cx={x + w + 10}
                          cy={y + h / 2}
                          r={6}
                          fill="#FFFFFF"
                          stroke="#3B0069"
                          strokeWidth={2}
                          className="cursor-crosshair"
                          onMouseDown={(event) =>
                            beginDependencyDrag(event, row, "right")
                          }
                          onClick={(event) => handleAnchorClick(event, row, "right")}
                        />
                        {hasTaskAuthority && (
                          <foreignObject
                            x={x + w + 24}
                            y={y - 3}
                            width={34}
                            height={32}
                          >
                            <div
                              xmlns="http://www.w3.org/1999/xhtml"
                              className="flex h-full w-full items-center justify-center"
                            >
                              <button
                                type="button"
                                className={clsx(
                                  "inline-flex h-7 w-7 items-center justify-center rounded-full border shadow-sm",
                                  dependencyEditorId === row.id
                                    ? "border-blue-500 bg-blue-600 text-white"
                                    : "border-blue-200 bg-white text-blue-700 hover:bg-blue-50",
                                )}
                                title="Edit dependencies"
                                onMouseDown={(event) => event.stopPropagation()}
                                onClick={(event) => openDependencyEditor(event, row.id)}
                              >
                                <Link2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </foreignObject>
                        )}
                      </>
                    )}
                  </g>
                );
              })}

              {rowScrollControls.map((control) => (
                <foreignObject
                  key={`scroll-control-${control.id}`}
                  x={control.x}
                  y={control.y}
                  width={32}
                  height={32}
                >
                  <div
                    xmlns="http://www.w3.org/1999/xhtml"
                    className="flex h-full w-full items-center justify-center"
                  >
                    <button
                      type="button"
                      className="flex h-8 w-8 items-center justify-center rounded border border-slate-300 bg-white text-slate-600 shadow-sm transition hover:border-[#3B0069] hover:bg-[#3B0069] hover:text-white"
                      title={control.label}
                      aria-label={control.label}
                      onClick={(event) => {
                        event.stopPropagation();
                        setSelectedId(control.id);
                        centerOnDate(control.targetDate);
                      }}
                    >
                      {control.side === "left" ? (
                        <ChevronLeft className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </foreignObject>
              ))}
  
              {Array.from(
                new Map(
                  [activeDateLabelRow, movingRow]
                    .filter(Boolean)
                    .map((row) => [row.id, row]),
                ).values(),
              ).map((row) => {
                if (!itemHasDates(row)) return null;
                const x = xForDate(row.start);
                const w = widthForSpan(row.start, row.end);
                const y = Math.max(2, row.top - 22);
                return (
                  <g key={`label-${row.id}`}>
                    <foreignObject x={Math.max(0, x - 86)} y={y} width={82} height={22}>
                      <div
                        xmlns="http://www.w3.org/1999/xhtml"
                        className="rounded bg-slate-900 px-1.5 py-0.5 text-center text-[10px] text-white"
                      >
                        {formatISODate(row.start)}
                      </div>
                    </foreignObject>
                    <foreignObject x={x + w + 4} y={y} width={108} height={22}>
                      <div
                        xmlns="http://www.w3.org/1999/xhtml"
                        className="rounded bg-slate-900 px-1.5 py-0.5 text-center text-[10px] text-white"
                      >
                        {formatISODate(row.end)} · {daysBetween(row.start, row.end)}d
                      </div>
                    </foreignObject>
                  </g>
                );
              })}
            </svg>

            {dependencyEditorId &&
              editedDependencyItem?.type === "task" &&
              dependencyEditorStyle && (
                <div
                  className="absolute z-40 max-h-[390px] overflow-auto rounded-xl border border-slate-200 bg-white p-4 shadow-2xl"
                  style={dependencyEditorStyle}
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs font-semibold uppercase text-slate-500">
                        Dependencies
                      </div>
                      <div
                        className="truncate text-base font-semibold text-slate-900"
                        title={editedDependencyItem.label}
                      >
                        {editedDependencyItem.label}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded text-slate-500 hover:bg-slate-100"
                      onClick={() => setDependencyEditorId(null)}
                      title="Close dependencies"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="space-y-5">
                    {renderDependencySection(
                      "predecessor",
                      "Predecessors",
                      ArrowUp,
                      editedPredecessors,
                      "No predecessors added yet",
                    )}

                    <div className="h-px bg-slate-200" />

                    {renderDependencySection(
                      "successor",
                      "Successors",
                      ArrowDown,
                      editedSuccessors,
                      "No successors added yet",
                    )}
                  </div>
                </div>
              )}
  
          </div>
        </div>
  
        {loading && (
          <div className="absolute bottom-5 right-6 rounded bg-white px-3 py-2 text-sm text-slate-700 shadow">
            Loading timeline...
          </div>
        )}
        {error && (
          <div className="absolute bottom-5 right-6 rounded bg-rose-600 px-3 py-2 text-sm text-white shadow">
            {error}
          </div>
        )}
      </div>
    );
  }
  
  export default GanttTimeline;
